// Service for packing material to product mapping CRUD operations
import api from './api';

export interface PackingMaterialMapping {
  id: number;
  product_item_number: string;
  packing_item_number: string;
  packing_level: 'case' | 'subunit';
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  product_name: string | null;
  product_chinese_name: string | null;
  packing_name: string | null;
  packing_chinese_name: string | null;
  packing_unit_weight: number | null;
}

export interface ProductItem {
  item_number: string;
  item_english_name: string | null;
  item_chinese_name: string | null;
}

export interface PackingMaterialSearchResult {
  item_number: string;
  item_english_name: string | null;
  item_chinese_name: string | null;
  unit_weight: number | null;
  item_unit: string | null;
}

// Packing material info returned by the /for-session endpoint for AddSessionModal display
export interface PackingMaterialForSession {
  packing_item_number: string;
  packing_level: 'case' | 'subunit';
  packing_name: string | null;
  packing_chinese_name: string | null;
  packing_unit_weight: number;
  item_sub_quantity: number;
  packing_info: string | null;
  item_unit: string | null;
  current_stock: number;
  available_pieces: number;
  available_units: number;
}

const packingMaterialMappingService = {
  async getPackingMaterialsForSession(productSku: string): Promise<{ materials: PackingMaterialForSession[]; product_sub_quantity: number }> {
    const response = await api.get('/production/packing-material-mappings/for-session', {
      params: { productSku },
    });
    return { materials: response.data.materials, product_sub_quantity: response.data.product_sub_quantity };
  },

  async getMappings(productItemNumber?: string): Promise<PackingMaterialMapping[]> {
    const params = productItemNumber ? { product: productItemNumber } : {};
    const response = await api.get('/production/packing-material-mappings', { params });
    return response.data.mappings;
  },

  async getProducts(): Promise<ProductItem[]> {
    const response = await api.get('/production/packing-material-mappings/products');
    return response.data.products;
  },

  async searchPackingMaterials(query: string): Promise<PackingMaterialSearchResult[]> {
    const response = await api.get('/production/packing-material-mappings/search-packing-materials', {
      params: { q: query },
    });
    return response.data.items;
  },

  async createMapping(data: {
    product_item_number: string;
    packing_item_number: string;
    packing_level?: 'case' | 'subunit';
    notes?: string;
    created_by?: string;
  }): Promise<PackingMaterialMapping> {
    const response = await api.post('/production/packing-material-mappings', data);
    return response.data.mapping;
  },

  async updateMapping(id: number, data: { packing_level?: 'case' | 'subunit'; notes?: string }): Promise<PackingMaterialMapping> {
    const response = await api.patch(`/production/packing-material-mappings/${id}`, data);
    return response.data.mapping;
  },

  async deleteMapping(id: number): Promise<void> {
    await api.delete(`/production/packing-material-mappings/${id}`);
  },
};

export default packingMaterialMappingService;
